<script setup lang="ts">
  import useVuelidate from "@vuelidate/core"
  import { email, required } from "@vuelidate/validators"
  import { INewsletterFooterCmsData } from "@/view-models/newsletter.model"
  import TextInput from "@/components/IcForm/TextInput.vue"

  const cmsData = CMSDataProvider.get<INewsletterFooterCmsData>(CMS_DATA_ID.NEWSLETTER_FOOTER)

  const firstname = ref("")
  const emailadress = ref("")
  const submitted = ref(false)
  const loading = ref(false)
  const submitSuccess = ref(false)
  const submitFailed = ref(false)

  const onFormSubmit = async () => {
    loading.value = true
    submitted.value = true

    const isFormCorrect = await v$.value.$validate()

    if (!isFormCorrect) {
      nextTick(() => {
        document.querySelector(".invalid-feedback")?.scrollIntoView({ block: "center", behavior: "smooth" })
      })

      loading.value = false

      return
    }

    try {
      await NewsletterService.Post({
        firstname: firstname.value,
        email: emailadress.value,
        culture: cmsData.culture,
        mailingKey: cmsData.mailingKey,
      })

      submitSuccess.value = true
    } catch (error) {
      submitFailed.value = true
    } finally {
      loading.value = false
    }
  }

  const rules = {
    firstname: {
      required,
    },
    emailadress: {
      required,
      email,
    },
  }

  const v$ = useVuelidate(rules, {
    firstname,
    emailadress,
  })
</script>

<template>
  <h3 class="label--sm mb-8">
    {{ cmsData.title }}
  </h3>
  <form @submit.prevent="onFormSubmit">
    <label
      for="newsletter-footer-firstname"
      class="block mb-2 label--xs"
    >
      {{ cmsData.nameLabel }}
    </label>
    <div class="flex">
      <TextInput
        id="newsletter-footer-firstname"
        v-model="firstname"
        class="w-10/12"
        :validation="{
          model: v$.firstname.$error,
          message: cmsData.requiredErrorMessage,
          submitted: submitted,
        }"
        :placeholder="cmsData.namePlaceholder"
        :input-props="{
          class:
            'h-12 w-full py-2.5 px-6 bg-wg-900 rounded-2xl placeholder:text-wg-300 placeholder:uppercase placeholder:text-label-sm placeholder:tracking-[2.24px]',
        }"
      />
    </div>
    <label
      for="newsletter-footer-email"
      class="block mb-2 label--xs"
    >
      {{ cmsData.emailLabel }}
    </label>
    <div class="flex gap-2">
      <TextInput
        id="newsletter-footer-email"
        v-model="emailadress"
        class="w-10/12"
        :validation="{
          model: v$.emailadress.$error,
          message: cmsData.requiredErrorMessage,
          submitted: submitted,
          invalid: [
            {
              value: v$.emailadress.email.$invalid,
              message: cmsData.validEmailErrorMessage,
            },
          ],
        }"
        :placeholder="cmsData.emailPlaceholder"
        :input-props="{
          class:
            'h-12 w-full py-2.5 px-6 bg-wg-900 rounded-2xl placeholder:text-wg-300 placeholder:uppercase placeholder:text-label-sm placeholder:tracking-[2.24px]',
        }"
      />
      <div class="my-4 flex justify-center items-start">
        <button
          type="submit"
          :aria-label="cmsData.buttonLabel"
          class="icon chevron-right size-12 shrink-0 rounded-2xl after:text-neutral-700 bg-wg-50"
        ></button>
      </div>
    </div>
    <div
      v-if="loading"
      class="themed-paragraph paragraph--lg"
    >
      {{ cmsData.loadingLabel }}
    </div>
    <div
      v-else-if="submitSuccess"
      class="themed-paragraph paragraph--lg"
    >
      {{ cmsData.submitSuccess }}
    </div>
    <div
      v-else-if="submitFailed"
      class="mt-2 text-red-600 paragraph--lg"
    >
      {{ cmsData.submitError }}
    </div>
  </form>
</template>
